import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Combobox,
    type ComboboxOption,
    type ComboboxOptionRenderMeta,
} from '@/components';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { useMembers } from '../hooks/useMembers';
import { LogsUserFilterRow } from './LogsUserFilterRow';

const RESULTS_LIMIT = 5;
const SEARCH_MIN_MEMBERS = 5;

interface LogsUserFilterProps {
    selected: string[];
    onChange: (userIds: string[]) => void;
    onClear: () => void;
}

export function LogsUserFilter({
    selected,
    onChange,
    onClear,
}: LogsUserFilterProps) {
    const { t } = useTranslation(I18nNamespace.SETTINGS);
    const [search, setSearch] = useState('');
    const { members, total, isLoaded, isLoading } = useMembers(
        RESULTS_LIMIT,
        1,
        search || undefined,
    );

    const [orgTotal, setOrgTotal] = useState<number | null>(null);
    useEffect(() => {
        if (isLoaded && !search && orgTotal === null) {
            setOrgTotal(total);
        }
    }, [isLoaded, search, orgTotal, total]);
    const showSearch = (orgTotal ?? 0) > SEARCH_MIN_MEMBERS;

    const options = members.map((member) => ({
        value: member.id,
        label: member.name,
    }));
    const emailById = Object.fromEntries(
        members.map((member) => [member.id, member.email]),
    );

    const toggle = (userId: string) => {
        onChange(
            selected.includes(userId)
                ? selected.filter((id) => id !== userId)
                : [...selected, userId],
        );
    };

    const renderOption = (
        option: ComboboxOption,
        { isSelected }: ComboboxOptionRenderMeta,
    ) => (
        <LogsUserFilterRow
            name={option.label}
            email={emailById[option.value] ?? ''}
            isSelected={isSelected}
        />
    );

    return (
        <Combobox
            title={t('logs.user')}
            options={isLoading ? [] : options}
            selected={selected}
            onToggle={toggle}
            onClear={selected.length > 0 ? onClear : undefined}
            onSearch={setSearch}
            isLoading={isLoading}
            searchPlaceholder={showSearch ? t('logs.searchUsers') : undefined}
            autoFocus={showSearch}
            emptyText={t('logs.noMatches')}
            clearLabel={t('logs.clearFilter')}
            renderOption={renderOption}
        />
    );
}
